import React, { useMemo, useState, useEffect } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, CheckCircleIcon, XCircleIcon, LockClosedIcon, LockOpenIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { computeLoanTermYears, calcGuaranteeFee, calcAnnualPmt, calcDSCR } from '../../utils/loanMath';

// Define TierRow interface (as provided by user)
// interface TierRow {
//   tier: 1 | 2 | 3;
//   rule: string;
//   maxPurchasePrice: number;
//   loanAmount: number;
//   annualDebtService: number;
//   dscrByYear: Record<string, number>;
// }

const TierCard = ({ tier, rule, maxPurchasePrice, loanAmount, annualDebtService, dscrByYear, color, onSelect, isSelected, isLocked, lenderAppetite, whyTier3Works }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(Number(value))) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value));
  };

  const formatDSCR = (value) => {
    if (value === undefined || value === null || isNaN(Number(value))) return 'N/A';
    return Number(value).toFixed(2) + 'x';
  }

  const yearKeys = Object.keys(dscrByYear).sort();

  const headerColorClasses = {
    green: 'bg-green-600',
    indigo: 'bg-indigo-600',
    amber: 'bg-amber-500',
  };

  return (
    <div className={`bg-white shadow-lg rounded-xl overflow-hidden border ${isSelected ? `border-${color}-500 ring-2 ring-${color}-500` : 'border-gray-200'} transition-all duration-300`}>
      <div className={`px-6 py-4 ${headerColorClasses[color]} text-white`}>
        <h3 className="text-lg font-semibold">Tier {tier}</h3>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-1">Lender Appetite</h4>
          <p className="text-sm text-gray-700">{lenderAppetite}</p>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-2 font-medium text-gray-600">Max Purchase Price</td>
              <td className="py-2 text-right font-semibold text-gray-800">{formatCurrency(maxPurchasePrice)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-2 font-medium text-gray-600">Est. Loan Amount</td>
              <td className="py-2 text-right font-semibold text-gray-800">{formatCurrency(loanAmount)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-2 font-medium text-gray-600">Annual Debt Service</td>
              <td className="py-2 text-right font-semibold text-gray-800">{formatCurrency(annualDebtService)}</td>
            </tr>
            <tr>
              <td className="py-2 font-medium text-gray-600">DSCR ({yearKeys.join(' / ')})</td>
              <td className="py-2 text-right font-semibold text-gray-800">
                {yearKeys.map(year => formatDSCR(dscrByYear[year])).join(' / ')}
              </td>
            </tr>
          </tbody>
        </table>

        {tier === 3 && whyTier3Works && (
          <div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
            >
              {isExpanded ? 'Hide Details' : 'Why Tier 3 Works'}
              {isExpanded ? <ChevronUpIcon className="h-4 w-4 ml-1" /> : <ChevronDownIcon className="h-4 w-4 ml-1" />}
            </button>
            {isExpanded && (
              <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs text-gray-600 border border-gray-200">
                <p className="font-semibold mb-1">EBITDA Bridge / Adjustments:</p>
                <p>{whyTier3Works}</p>
              </div>
            )}
          </div>
        )}

        {!isLocked && (
          <button
            onClick={onSelect}
            disabled={isSelected}
            className={`w-full mt-4 px-4 py-2 text-sm font-medium rounded-md transition-colors
              ${isSelected 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : `bg-${color}-500 hover:bg-${color}-600 text-white focus:ring-2 focus:ring-offset-2 focus:ring-${color}-400`}
            `}
          >
            {isSelected ? 'Selected' : 'Select This Scenario'}
          </button>
        )}
      </div>
    </div>
  );
};

const PrequalificationTable = ({ ocrData = {}, loanData = {}, isLocked = true, onUnlockRequest }) => {
  const [activeTab, setActiveTab] = useState('withSalary'); // 'withSalary' or 'noSalary'
  const [selectedTierInfo, setSelectedTierInfo] = useState(null); // Stores { tier: number, hasSalary: boolean, prequalifiedAmount: number }
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (loanData && loanData.prequalificationType && typeof loanData.prequalificationWithSalary === 'boolean') {
      const tierMatch = loanData.prequalificationType.match(/Tier (\d+)/);
      if (tierMatch && tierMatch[1]) {
        setSelectedTierInfo({
          tier: parseInt(tierMatch[1], 10),
          hasSalary: loanData.prequalificationWithSalary,
          prequalifiedAmount: loanData.prequalifiedAmount,
          // loanAmount: loanData.loanAmount // Assuming loanAmount is part of loanData if saved previously
        });
        setActiveTab(loanData.prequalificationWithSalary ? 'withSalary' : 'noSalary');
      }
    }
  }, [loanData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const years = useMemo(() => {
    if (!ocrData || Object.keys(ocrData).length === 0) return [];
    return Object.keys(ocrData).sort((a, b) => parseInt(a) - parseInt(b)); // Assuming years are numeric strings like "2022"
  }, [ocrData]);

  const mostRecentYear = useMemo(() => years.length > 0 ? years[years.length - 1] : null, [years]);
  const priorYear = useMemo(() => years.length > 1 ? years[years.length - 2] : null, [years]);

  // Helper functions for NOI calculation (adapted from PrequalFinancialMetricsDisplay.js)
  const calculateEBITDAOwnersCompInternal = (yearData) => {
    if (!yearData) return 0;
    const baseIncome = Number(yearData.ordinary_business_income || 0);
    const originalOfficerCompensation = Number(yearData.compensation_of_officers || 0);
    const interest = Number(yearData.interest || 0);
    const depreciation = Number(yearData.depreciation || 0);
    const amortization = Number(yearData.amortization || 0);
    return baseIncome + originalOfficerCompensation + interest + depreciation + amortization;
  };

  const calculateAddBacksInternal = (yearData) => {
    if (!yearData || !yearData.other_deductions) return 0;
    const otherDeductions = yearData.other_deductions;
    if (Array.isArray(otherDeductions)) {
      return otherDeductions.reduce((total, deduction) => {
        return total + Number(deduction.amount || 0);
      }, 0);
    }
    // Optional: Handle object-style other_deductions if necessary
    if (typeof otherDeductions === 'object' && otherDeductions !== null) {
        return Object.entries(otherDeductions).reduce((total, [/*category*/, amount]) => {
            return total + Number(amount || 0);
        }, 0);
    }
    return 0;
  };

  const calculateSDEInternal = (yearData) => {
    if (!yearData) return 0;
    const ebitdaOwnersComp = calculateEBITDAOwnersCompInternal(yearData);
    const addbacksTotal = calculateAddBacksInternal(yearData);
    return ebitdaOwnersComp + addbacksTotal;
  };

  // New NOI calculation function, replacing calculateAdjustedEBITDA
  const calculateNOIForPrequalification = (year, includeSalary) => {
    const yearData = ocrData[year]; // ocrData from component props
    if (!yearData) return 0;

    const sde = calculateSDEInternal(yearData);

    if (includeSalary) { // "With $75k Owner Salary" scenario
      return sde - 75000; 
    } else { // "No Owner Salary" scenario
      return sde; 
    }
  };

  const calculateLoanMetrics = (purchasePrice) => {
    if (isNaN(purchasePrice) || purchasePrice <= 0) {
      return { loanAmount: 0, annualDebtService: 0, termYears: 0 };
    }
    const sba7aPercentage = 0.90; // Standard LTV for business acquisition
    const baseLoanAmount = Number(purchasePrice) * sba7aPercentage;
    
    const guaranteeFee = calcGuaranteeFee(baseLoanAmount);
    const packagingFee = 2500; // Typical
    const closingCosts = 10000; // Typical
    const totalLoanAmount = baseLoanAmount + guaranteeFee + packagingFee + closingCosts;
    
    // Use Prime + 2.75% (approx 8.5% + 2.75% = 11.25% as of mid-2024)
    // User example used 9.5% and 10.0%. Let's use an average or make it configurable if needed.
    // For now, let's stick to a common SBA rate.
    const interestRate = loanData.prequalificationType === 'Easy Financing' ? 0.095 : 0.10; // From original prequalDetails, might need update
                                                                                      // User example: 10.5%
                                                                                      // Tier 1: best pricing, Tier 2: small premium. Let's use 0.10 as base.
    
    const realEstateValue = Number(loanData?.realEstateValue || 0);
    const termYears = computeLoanTermYears({ purchasePrice: Number(purchasePrice), realEstateValue });

    if (totalLoanAmount <= 0) {
      return { loanAmount: 0, annualDebtService: 0, termYears: termYears };
    }
    const annualDebtService = calcAnnualPmt({ principal: totalLoanAmount, rate: interestRate, termYears });
    return { loanAmount: totalLoanAmount, annualDebtService, termYears };
  };

  const calculateDSCRForYear = (year, purchasePrice, includeSalary) => {
    const noiForYear = calculateNOIForPrequalification(year, includeSalary);
    const { annualDebtService } = calculateLoanMetrics(purchasePrice);
    if (annualDebtService === 0) return 0; // Avoid division by zero
    return noiForYear / annualDebtService;
  };

  const getDscrByYear = (purchasePrice, includeSalary) => {
    const dscrMap = {};
    years.forEach(year => {
      dscrMap[year] = calculateDSCRForYear(year, purchasePrice, includeSalary);
    });
    return dscrMap;
  };
  
  // Iterative function to find max purchase price for a given DSCR rule
  const findMaxPurchasePriceByRule = (includeSalary, ruleFn) => {
    let low = 0;
    let high = 10000000; // Max possible price, e.g., $10MM
    let bestPrice = 0;
    const iterations = 100; // For precision in binary search
    
    for (let i = 0; i < iterations; i++) {
        const mid = (low + high) / 2;
        if (mid <= 0) { // Ensure mid is positive to avoid issues with loan calcs
          low = 1; // or some small positive number
          continue;
        }
        const dscrs = getDscrByYear(mid, includeSalary);
        if (ruleFn(dscrs)) {
            bestPrice = mid;
            low = mid;
        } else {
            high = mid;
        }
    }
    // Round to nearest $1000 for cleaner display
    return Math.round(bestPrice / 1000) * 1000;
  };

  const tierData = useMemo(() => {
    const scenarios = {
      withSalary: [],
      noSalary: []
    };

    [true, false].forEach(hasSalary => {
      const currentScenario = hasSalary ? 'withSalary' : 'noSalary';

      // Tier 1 Rule: All fiscal / TTM periods >= 1.15x
      const tier1MaxPP = findMaxPurchasePriceByRule(hasSalary, (dscrByYear) => 
        years.every(year => dscrByYear[year] >= 1.15)
      );
      const tier1LoanMetrics = calculateLoanMetrics(tier1MaxPP);
      scenarios[currentScenario].push({
        tier: 1,
        rule: "All fiscal / TTM periods ≥ 1.15×",
        maxPurchasePrice: tier1MaxPP,
        loanAmount: tier1LoanMetrics.loanAmount,
        annualDebtService: tier1LoanMetrics.annualDebtService,
        dscrByYear: getDscrByYear(tier1MaxPP, hasSalary),
        color: 'green',
        lenderAppetite: "Broadest set of PLP Lenders; best pricing and advance rates"
      });

      // Tier 2 Rule: Most recent year >= 1.50x AND prior year >= 1.00x
      const tier2MaxPP = findMaxPurchasePriceByRule(hasSalary, (dscrByYear) => {
        if (!mostRecentYear || !priorYear) return false;
        return dscrByYear[mostRecentYear] >= 1.50 && dscrByYear[priorYear] >= 1.00;
      });
      const tier2LoanMetrics = calculateLoanMetrics(tier2MaxPP);
      scenarios[currentScenario].push({
        tier: 2,
        rule: "Most recent year ≥ 1.50× AND prior year ≥ 1.00×",
        maxPurchasePrice: tier2MaxPP,
        loanAmount: tier2LoanMetrics.loanAmount,
        annualDebtService: tier2LoanMetrics.annualDebtService,
        dscrByYear: getDscrByYear(tier2MaxPP, hasSalary),
        color: 'indigo',
        lenderAppetite: "Majority of mainstream SBA 7(a) lenders"
      });

      // Tier 3 Rule: Most recent year >= 1.20x with a clear narrative
      const tier3MaxPP = findMaxPurchasePriceByRule(hasSalary, (dscrByYear) => {
        if (!mostRecentYear) return false;
        return dscrByYear[mostRecentYear] >= 1.20;
      });
      const tier3LoanMetrics = calculateLoanMetrics(tier3MaxPP);
      scenarios[currentScenario].push({
        tier: 3,
        rule: "Most recent year ≥ 1.20× with a clear narrative for margin change",
        maxPurchasePrice: tier3MaxPP,
        loanAmount: tier3LoanMetrics.loanAmount,
        annualDebtService: tier3LoanMetrics.annualDebtService,
        dscrByYear: getDscrByYear(tier3MaxPP, hasSalary),
        color: 'amber',
        lenderAppetite: "Niche SBA lenders and a Strong Buyer",
        whyTier3Works: loanData?.tier3Narrative || "Detailed explanation of EBITDA adjustments, one-off expenses, or pro forma projections for underwriter review." // Placeholder
      });
    });
    return scenarios;
  }, [ocrData, loanData, years, mostRecentYear, priorYear]);

  const handleSelectTier = async (tier, hasSalary, maxPurchasePrice, loanAmount) => {
    if (isLocked) {
      onUnlockRequest?.();
      return;
    }
    setErrorMsg('');
    setSaveSuccess(false);
    setIsSaving(true);

    const newSelectedInfo = { tier, hasSalary, prequalifiedAmount: maxPurchasePrice, loanAmount };

    try {
      const prequalDocRef = doc(db, 'prequalificationApplications', loanData.id);
      await updateDoc(prequalDocRef, {
        prequalifiedAmount: maxPurchasePrice,
        loanAmount: loanAmount, // Save calculated loan amount
        prequalificationType: `Tier ${tier}`,
        prequalificationWithSalary: hasSalary,
        status: "Prequalified",
        lastUpdated: serverTimestamp()
      });
      setSelectedTierInfo(newSelectedInfo);
      setSaveSuccess(true);
      // Update loanData locally if needed or rely on parent component re-fetch
      if (loanData.onPrequalificationUpdate) { // Assuming a callback to update parent
         loanData.onPrequalificationUpdate({
           ...loanData,
           prequalifiedAmount: maxPurchasePrice,
           loanAmount: loanAmount,
           prequalificationType: `Tier ${tier}`,
           prequalificationWithSalary: hasSalary,
           status: "Prequalified"
         });
      }
    } catch (error) {
      console.error("Error updating prequalification:", error);
      setErrorMsg("Failed to save selection. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!ocrData || Object.keys(ocrData).length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">No OCR data available to perform prequalification analysis.</p>
      </div>
    );
  }
  
  const currentTiers = tierData[activeTab];

  return (
    <div className="p-4 sm:p-6 bg-gray-50 rounded-lg">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center">
        <div className="flex border border-gray-300 rounded-md p-1 bg-white shadow-sm mb-4 sm:mb-0">
          <button
            onClick={() => setActiveTab('withSalary')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors
              ${activeTab === 'withSalary' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            With $75k Owner Salary
          </button>
          <button
            onClick={() => setActiveTab('noSalary')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors
              ${activeTab === 'noSalary' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            No Owner Salary
          </button>
        </div>
        <div className="flex items-center">
          {isLocked ? (
            <button
              onClick={onUnlockRequest}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <LockOpenIcon className="h-4 w-4 mr-1" />
              Unlock Selection
            </button>
          ) : (
             <p className="text-sm text-green-600 font-medium flex items-center">
                <LockOpenIcon className="h-4 w-4 mr-1 text-green-500"/> Selection Unlocked
             </p>
          )}
        </div>
      </div>

      {isSaving && <p className="text-sm text-blue-600 mb-2">Saving selection...</p>}
      {saveSuccess && <p className="text-sm text-green-600 mb-2">Selection saved successfully!</p>}
      {errorMsg && <p className="text-sm text-red-600 mb-2">{errorMsg}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentTiers.map((tierInfo) => (
          <TierCard
            key={`${activeTab}-tier-${tierInfo.tier}`}
            {...tierInfo}
            isSelected={selectedTierInfo?.tier === tierInfo.tier && selectedTierInfo?.hasSalary === (activeTab === 'withSalary')}
            isLocked={isLocked}
            onSelect={() => handleSelectTier(tierInfo.tier, activeTab === 'withSalary', tierInfo.maxPurchasePrice, tierInfo.loanAmount)}
          />
        ))}
      </div>
      {currentTiers.every(t => t.maxPurchasePrice === 0) && (
         <div className="mt-6 text-center text-gray-500 col-span-full">
            <p>No viable prequalification scenarios found for the current financial data and selected owner salary option.</p>
            <p>Please review the OCR data or try adjusting the owner salary option.</p>
         </div>
      )}
    </div>
  );
};

export default PrequalificationTable;